import Component from './component';
import Hex from './hex';
import LinearGradient from './linear-gradient';

/** Text is the base text rendering component, that you would extend from to create labels, paragraphs, etc. */
export default class Text extends Component {
	public static override observedAttributes = ['cursor', 'height', 'max-height', 'max-width', 'min-height', 'min-width', 'opacity', 'width', 'text', 'text-align-horizontal', 'font-style'];
	/**
	 * The color CSS property sets the foreground color value of an element's text and text decorations, and sets the currentcolor value. currentcolor may be used as an indirect value on other properties and is the default for other color properties, such as border-color.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/color} for syntax
	 */
	public set fill(value: Hex | LinearGradient | null) {
		assertIsValidFill(value);
		this.#fill = value;
		this.#fillChanged = true;
		this.invalidateProperties();
	}

	public get fill(): Hex | LinearGradient | null {
		return this.#fill;
	}

	/**
	 * The font-family CSS property specifies a prioritized list of one or more font family names and/or generic family names for the selected element.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-family} for syntax
	 */
	public set fontFamily(value: string) {
		assertIsString(value);
		this.#fontFamily = value;
		this.#fontFamilyChanged = true;
		this.invalidateProperties();
	}

	public get fontFamily(): string {
		return this.#fontFamily;
	}

	/**
	 * The font size in pixels
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-size} for syntax
	 */
	public set fontSize(value: number) {
		assertIsNonNegative(value);
		this.#fontSize = value;
		this.#fontSizeChanged = true;
		this.invalidateProperties();
	}

	public get fontSize(): number {
		return this.#fontSize;
	}

	/**
	 * The font-style CSS property sets whether a font should be styled with a normal, italic, or oblique face from its font-family.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/font-style} for syntax
	 */
	public set fontStyle(value: 'italic' | 'normal') {
		if (this.#fontStyle === value) {
			return;
		}
		this.#fontStyle = value;
		this.#fontStyleChanged = true;
		this.invalidateProperties();
	}

	public get fontStyle(): 'italic' | 'normal' {
		return this.#fontStyle;
	}

	/**
	 * The font-weight CSS property sets the weight (or boldness) of the font. Valid values are from 1 to 1000.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight} for syntax
	 */
	public set fontWeight(value: number) {
		assertIsFromOneToThousand(value);
		this.#fontWeight = value;
		this.#fontWeightChanged = true;
		this.invalidateProperties();
	}

	public get fontWeight(): number {
		return this.#fontWeight;
	}

	/**
	 * The letter-spacing CSS property sets the horizontal spacing behavior between text text. This value is added to the natural spacing between text while rendering the text. Positive values of letter-spacing causes text to spread farther apart, while negative values of letter-spacing bring text closer together.
	 * Valid values are positive and negative numbers in pixels.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/letter-spacing} for syntax
	 */
	public set letterSpacing(value: number) {
		assertIsNumber(value);
		this.#letterSpacing = value;
		this.#letterSpacingChanged = true;
		this.invalidateProperties();
	}

	public get letterSpacing(): number {
		return this.#letterSpacing;
	}

	/**
	 * The line-height CSS property sets the height of a line box in horizontal writing modes. In vertical writing modes, it sets the width of a line box. It's commonly used to set the distance between lines of text. On block-level elements in horizontal writing modes, it specifies the preferred height of line boxes
	 * within the element, and on non-replaced inline elements, it specifies the height that is used to calculate line box height.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/line-height} for syntax
	 */
	public set lineHeight(value: 'auto' | number) {
		assertIsValidLineHeight(value);
		this.#lineHeight = value;
		this.#lineHeightChanged = true;
		this.invalidateProperties();
	}

	public get lineHeight(): 'auto' | number {
		return this.#lineHeight;
	}

	/**
	 * The line-clamp CSS property allows limiting of the contents of a block to the specified number of lines.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/line-clamp} for syntax
	 */
	public set maxLines(value: number) {
		assertIsPositiveInteger(value);
		this.#maxLines = value;
		this.#maxLinesChanged = true;
		this.invalidateProperties();
	}

	public get maxLines(): number {
		return this.#maxLines;
	}

	/**
	 * Whether a line break in the text is kept as one on screen, or collapsed into a space the way the browser collapses whitespace by default.
	 *
	 * Off by default, since text that is typed or labelled is one run of words. On for text that came with its lines already drawn — a message from an engine, say — where the breaks are part of what it says. Only the breaks are kept: runs of spaces still collapse, so this is `white-space: pre-line` and not `pre`.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/white-space} for syntax
	 */
	public set preserveLineBreaks(value: boolean) {
		assertIsBoolean(value);
		this.#preserveLineBreaks = value;
		this.#preserveLineBreaksChanged = true;
		this.invalidateProperties();
	}

	public get preserveLineBreaks(): boolean {
		return this.#preserveLineBreaks;
	}

	/**
	 * The user-select CSS property controls whether the user can select text. This doesn't have any effect on content loaded as part of a browser's user interface (its chrome), except in textboxes.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/user-select} for syntax
	 */
	public set selectable(value: boolean) {
		assertIsBoolean(value);
		this.#selectable = value;
		this.#selectableChanged = true;
		this.invalidateProperties();
	}

	public get selectable(): boolean {
		return this.#selectable;
	}

	/**
	 * The slashedZero property is part of the font-feature-settings, that will show the zero with a slash if set to true.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-feature-settings} for syntax
	 */
	public set slashedZero(value: boolean) {
		if (this.#slashedZero === value) {
			return;
		}
		assertIsBoolean(value);
		this.#slashedZero = value;
		this.#slashedZeroChanged = true;
		this.invalidateProperties();
	}

	public get slashedZero(): boolean {
		return this.#slashedZero;
	}

	/**
	 * The tabularFigures is part of the font-feature-settings, that will ensure that numbers has mono space size if set to true.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/font-feature-settings} for syntax
	 */
	public set tabularFigures(value: boolean) {
		if (this.#tabularFigures === value) {
			return;
		}
		assertIsBoolean(value);
		this.#tabularFigures = value;
		this.#tabularFiguresChanged = true;
		this.invalidateProperties();
	}

	public get tabularFigures(): boolean {
		return this.#tabularFigures;
	}

	/** The text displayed to the user. */
	public set text(value: string) {
		assertIsString(value);
		this.#text = value;
		this.#textChanged = true;
		this.invalidateProperties();
	}

	/** The raw text in the text Component. */
	public get text(): string {
		return this.#text;
	}

	/**
	 * The text-align CSS property sets the horizontal alignment of the inline-level content inside a block element. This means it works like vertical-align but in the horizontal direction.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/text-align} for syntax
	 */
	public set textAlignHorizontal(value: 'center' | 'justified' | 'left' | 'right') {
		assertIsValidTextAlignHorizontal(value);
		this.#textAlignHorizontal = value;
		this.#textAlignHorizontalChanged = true;
		this.invalidateProperties();
	}

	public get textAlignHorizontal(): 'center' | 'justified' | 'left' | 'right' {
		return this.#textAlignHorizontal;
	}

	/**
	 * The truncate property, sets if the text should be cut off and show three dots (...), if there is not enough space to show all the text.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/text-overflow} for syntax
	 */
	public set truncate(value: boolean) {
		assertIsBoolean(value);
		this.#truncate = value;
		this.#truncateChanged = true;
		this.invalidateProperties();
	}

	public get truncate(): boolean {
		return this.#truncate;
	}

	/** Visible completely hides the component when set to false, otherwise shown. */
	override set visible(value: boolean) {
		assertIsValidVisible(value);
		this.#visible = value;
		this.#visibleChanged = true;
		this.invalidateProperties();
	}

	override get visible(): boolean {
		return this.#visible;
	}

	/**
	 * The text-box CSS property is a shorthand that corresponds to the text-box-trim and text-box-edge properties, which together specify the amount of space to trim from the block-start edge and block-end edge of a text element's block container.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/text-box} for syntax
	 */

	public set verticalTrim(value: 'cap' | 'standard') {
		if (this.#verticalTrim === value) {
			return;
		}
		assertIsValidVerticalTrim(value);
		this.#verticalTrim = value;
		this.#verticalTrimChanged = true;
		this.invalidateProperties();
	}

	public get verticalTrim(): 'cap' | 'standard' {
		return this.#verticalTrim;
	}

	#fill: Hex | LinearGradient | null;

	#fillChanged: boolean;

	#fontFamily: string;

	#fontFamilyChanged: boolean;

	#fontSize: number;

	#fontSizeChanged: boolean;

	#fontStyle: 'italic' | 'normal';

	#fontStyleChanged: boolean;

	#fontWeight: number;

	#fontWeightChanged: boolean;

	#letterSpacing: number;

	#letterSpacingChanged: boolean;

	#lineHeight: 'auto' | number;

	#lineHeightChanged: boolean;

	#maxLines: number;

	#maxLinesChanged: boolean;

	#preserveLineBreaks: boolean;

	#preserveLineBreaksChanged: boolean;

	#selectable: boolean;

	#selectableChanged: boolean;

	#slashedZero: boolean;

	#slashedZeroChanged: boolean;

	#tabularFigures: boolean;

	#tabularFiguresChanged: boolean;

	#text: string;

	#textAlignHorizontal: 'center' | 'justified' | 'left' | 'right';

	#textAlignHorizontalChanged: boolean;

	#textChanged: boolean;

	#truncate: boolean;

	#truncateChanged: boolean;

	#visible: boolean;

	#visibleChanged: boolean;

	#verticalTrim: 'cap' | 'standard';

	#verticalTrimChanged: boolean;

	public constructor() {
		super();
		this.#text = '';
		this.#textChanged = false;
		this.#fill = null;
		this.#fillChanged = false;
		this.#fontFamily = '';
		this.#fontFamilyChanged = false;
		this.#fontSize = 16;
		this.#fontSizeChanged = false;
		this.#fontStyle = 'normal';
		this.#fontStyleChanged = false;
		this.#fontWeight = 400;
		this.#fontWeightChanged = false;
		this.#letterSpacing = 0;
		this.#letterSpacingChanged = false;
		this.#lineHeight = 'auto';
		this.#lineHeightChanged = false;
		this.#maxLines = 1;
		this.#maxLinesChanged = false;
		this.#preserveLineBreaks = false;
		this.#preserveLineBreaksChanged = false;
		this.#selectable = false;
		this.#selectableChanged = false;
		this.#slashedZero = false;
		this.#slashedZeroChanged = false;
		this.#tabularFigures = false;
		this.#tabularFiguresChanged = false;
		this.#textAlignHorizontal = 'left';
		this.#textAlignHorizontalChanged = false;
		this.#truncate = false;
		this.#truncateChanged = false;
		this.#visible = true;
		this.#visibleChanged = false;
		this.#verticalTrim = 'standard';
		this.#verticalTrimChanged = false;
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'text') {
			this.#textAttributeChanged(value);
		} else if (name === 'text-align-horizontal') {
			this.#textAlignHorizontalAttributeChanged(value);
		} else if (name === 'font-style') {
			this.#fontStyleAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#textChanged) {
			this.#commitText();
		}
		if (this.#fillChanged) {
			this.#commit_fill();
		}
		this.#commitFontProperties();
		if (this.#preserveLineBreaksChanged) {
			this.#commitPreserveLineBreaks();
		}
		if (this.#selectableChanged) {
			this.#commitSelectable();
		}
		this.#commitAlignProperties();
		if (this.#truncateChanged || this.#visibleChanged) {
			this.#commitTruncateVisible();
		}
		if (this.#slashedZeroChanged || this.#tabularFiguresChanged) {
			this.#commitSlashZeroAndTabularFigures();
		}
		if (this.#maxLinesChanged) {
			this.#commitMaxLines();
		}
		if (this.#verticalTrimChanged) {
			this.#commitVerticalTrim();
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.#setDefaultStyles();
	}

	#commit_fill(): void {
		this.#fillChanged = false;
		this.style.color = '';
		if (this.fill instanceof LinearGradient) {
			this.style.background = this.fill.toStyleString();
			this.style.webkitTextFillColor = 'transparent';
			this.style.backgroundClip = 'text';
			return;
		}
		this.style.background = '';
		this.style.webkitTextFillColor = '';
		this.style.backgroundClip = '';
		if (this.fill instanceof Hex) {
			this.style.color = this.fill.toStyleString();
		}
	}

	#commitAlignProperties(): void {
		if (this.#textAlignHorizontalChanged) {
			this.#commitTextAlignHorizontal();
		}
	}

	#commitFontFamily(): void {
		this.#fontFamilyChanged = false;
		this.style.fontFamily = this.fontFamily;
	}

	#commitFontProperties(): void {
		if (this.#fontFamilyChanged) {
			this.#commitFontFamily();
		}
		if (this.#fontSizeChanged) {
			this.#commitFontSize();
		}
		if (this.#fontWeightChanged) {
			this.#commitFontWeight();
		}
		if (this.#letterSpacingChanged) {
			this.#commitLetterSpacing();
		}
		if (this.#lineHeightChanged) {
			this.#commitLineHeight();
		}
		if (this.#fontStyleChanged) {
			this.#commitFontStyle();
		}
	}

	#commitFontSize(): void {
		this.#fontSizeChanged = false;
		this.style.fontSize = `${this.fontSize}px`;
	}

	#commitFontStyle(): void {
		this.#fontStyleChanged = false;
		this.style.fontStyle = this.#fontStyle;
	}

	#commitFontWeight(): void {
		this.#fontWeightChanged = false;
		this.style.fontWeight = `${this.fontWeight}`;
	}

	#commitLetterSpacing(): void {
		this.#letterSpacingChanged = false;
		this.style.letterSpacing = `${this.letterSpacing}px`;
	}

	#commitLineHeight(): void {
		this.#lineHeightChanged = false;
		if (this.lineHeight === 'auto') {
			this.style.lineHeight = '1.2';
			return;
		}
		this.style.lineHeight = `${this.lineHeight}px`;
	}

	#commitMaxLines(): void {
		this.#maxLinesChanged = false;
		this.style.webkitLineClamp = this.maxLines.toString();
	}

	#commitPreserveLineBreaks(): void {
		this.#preserveLineBreaksChanged = false;
		this.style.whiteSpace = this.#preserveLineBreaks ? 'pre-line' : '';
	}

	#commitSelectable(): void {
		this.#selectableChanged = false;
		this.style.userSelect = this.#selectable ? '' : 'none';
	}

	#commitSlashZeroAndTabularFigures(): void {
		this.#slashedZeroChanged = false;
		this.#tabularFiguresChanged = false;
		if (this.slashedZero === true && this.tabularFigures === true) {
			this.style.fontFeatureSettings = '"zero" 1, "tnum" 1';
		} else if (this.slashedZero === true && this.tabularFigures === false) {
			this.style.fontFeatureSettings = '"zero" 1';
		} else if (this.slashedZero === false && this.tabularFigures === true) {
			this.style.fontFeatureSettings = '"tnum" 1';
		} else {
			this.style.fontFeatureSettings = '';
		}
	}

	#commitText(): void {
		this.#textChanged = false;
		this.textContent = this.text;
	}

	#commitTextAlignHorizontal(): void {
		this.#textAlignHorizontalChanged = false;
		if (this.textAlignHorizontal === 'left') {
			this.style.textAlign = 'start';
			this.style.justifyContent = 'flex-start';
			return;
		}
		if (this.textAlignHorizontal === 'right') {
			this.style.textAlign = 'end';
			this.style.justifyContent = 'flex-end';
			return;
		}
		if (this.textAlignHorizontal === 'justified') {
			this.style.textAlign = 'justify';
			this.style.justifyContent = 'space-around';
			return;
		}
		if (this.textAlignHorizontal === 'center') {
			this.style.textAlign = 'center';
			this.style.justifyContent = 'center';
		}
	}

	#commitTruncateVisible(): void {
		this.#truncateChanged = false;
		this.#visibleChanged = false;
		if (this.#visible) {
			if (this.#truncate) {
				this.style.overflow = 'hidden';
				this.style.webkitBoxOrient = 'vertical';
				this.style.display = '-webkit-box';
			} else {
				this.style.overflow = 'visible';
				this.style.webkitBoxOrient = '';
				this.style.display = 'inline-flex';
			}
		} else {
			this.style.display = 'none';
		}
	}

	#fontStyleAttributeChanged(value: string): void {
		if (value === 'normal' || value === 'italic') {
			this.fontStyle = value;
		}
	}

	#setDefaultStyles(): void {
		this.style.alignItems = 'flex-start';
		this.style.color = '';
		this.style.display = 'inline-flex';
		this.style.fontFamily = '';
		this.style.fontSize = '16px';
		this.style.fontWeight = '400';
		this.style.lineHeight = '1.2';
		this.style.textAlign = 'start';
		this.style.userSelect = 'none';
		this.style.justifyContent = 'flex-start';
		this.style.fontStyle = 'normal';
		this.style.background = '';
		this.style.overflow = 'visible';
		this.style.backgroundClip = '';
		this.style.webkitLineClamp = '1';
		this.style.webkitTextFillColor = '';
	}

	#textAlignHorizontalAttributeChanged(value: string): void {
		assertIsValidTextAlignHorizontal(value);
		this.textAlignHorizontal = value;
	}

	#textAttributeChanged(value: string): void {
		this.text = value;
	}

	#commitVerticalTrim(): void {
		this.#verticalTrimChanged = false;
		if (this.verticalTrim === 'standard') {
			this.style.textBoxTrim = '';
			this.style.textBoxEdge = '';
			return;
		}
		this.style.textBoxTrim = 'trim-both';
		this.style.textBoxEdge = 'cap alphabetic';
	}
}
customElements.define('ac-text', Text);

function assertIsValidVerticalTrim(value: unknown): asserts value is 'cap' | 'standard' {
	if (value === 'standard' || value === 'cap') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "standard" or "cap"`);
}

function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be true or false`);
}

function assertIsFromOneToThousand(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 1 && value <= 1000) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be from 1 to 1000`);
}

function assertIsNonNegative(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a non negative number`);
}

function assertIsNumber(value: unknown): asserts value is number {
	if (typeof value === 'number') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be a number type`);
}

function assertIsPositiveInteger(value: unknown): asserts value is number {
	if (typeof value === 'number' && value >= 1 && String(value).includes('.') === false) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be a positive integer`);
}

function assertIsString(value: unknown): asserts value is string {
	if (typeof value === 'string') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type string`);
}

function assertIsValidFill(value: unknown): asserts value is Hex | null {
	if (value instanceof Hex || value instanceof LinearGradient || value === null) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be null or an instance of Hex`);
}

function assertIsValidLineHeight(value: unknown): asserts value is 'auto' | number {
	if (value === 'auto') {
		return;
	}
	if (typeof value === 'number' && value > 0) {
		return;
	}
	throw new RangeError(`[${String(value)}] is invalid, must be more than 0 or "auto"`);
}

function assertIsValidTextAlignHorizontal(value: unknown): asserts value is 'center' | 'justified' | 'left' | 'right' {
	if (value === 'left' || value === 'center' || value === 'right' || value === 'justified') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "left", "center", "right" or "justified"`);
}

function assertIsValidVisible(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be boolean`);
}
